import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'

// Import organized translations
import { common } from './locales/en/common'
import { dashboard } from './locales/en/dashboard'
import { vehicles } from './locales/en/vehicles'
import { calendar } from './locales/en/calendar'
import { agency } from './locales/en/agency'
import { auth } from './locales/en/auth'
import { cars } from './locales/en/cars'
import { admin } from './locales/en/admin'

// French translations
import { common as commonFr } from './locales/fr/common'
import { dashboard as dashboardFr } from './locales/fr/dashboard'
import { vehicles as vehiclesFr } from './locales/fr/vehicles'
import { calendar as calendarFr } from './locales/fr/calendar'
import { agency as agencyFr } from './locales/fr/agency'
import { auth as authFr } from './locales/fr/auth'
import { cars as carsFr } from './locales/fr/cars'
import { admin as adminFr } from './locales/fr/admin'

// Arabic translations
import { common as commonAr } from './locales/ar/common'
import { dashboard as dashboardAr } from './locales/ar/dashboard'
import { vehicles as vehiclesAr } from './locales/ar/vehicles'
import { calendar as calendarAr } from './locales/ar/calendar'
import { agency as agencyAr } from './locales/ar/agency'
import { auth as authAr } from './locales/ar/auth'
import { cars as carsAr } from './locales/ar/cars'
import { admin as adminAr } from './locales/ar/admin'

// Import main translation bundles that include agenciesPage
import { en } from './locales/en/index'
import { fr } from './locales/fr/index'
import { ar } from './locales/ar/index'

// Translation resources with proper namespaces
const resources = {
  en: {
    common,
    dashboard,
    vehicles,
    calendar,
    agency,
    auth,
    cars,
    admin,
    translation: {
      // Keep existing main translations
      ...common,
      ...auth,
      // Include the full translation bundle with agenciesPage
      ...en.translation,
      // Provide nested auth namespace for components using 'auth.xxx' keys
      auth,
      // Duplicate vehicles namespace under defaultNS for components using 'vehicles.xxx' keys
      vehicles,
      // Duplicate agency namespace under defaultNS for components using 'agency.xxx' keys
      agency,
      // Duplicate dashboard namespace under defaultNS for components using 'dashboard.xxx' keys
      dashboard,
      // Duplicate common namespace under defaultNS for components using 'common.xxx' keys
      common,
      // Duplicate cars namespace under defaultNS for components using 'cars.xxx' keys
      cars,
      // Duplicate admin namespace under defaultNS for components using 'admin.xxx' keys
      admin,
      
      // Hero Section
      heroTitle: 'Find Your Perfect Rental Car',
      heroSubtitle: 'Discover the best car rental deals from trusted agencies worldwide',
      searchCars: 'Search Cars',
      findBookRental: 'Find, book, and rental car in',
      easySteps: 'Easy steps.',
      getCarWherever: 'Get a car wherever and whenever you need it with professional service and competitive prices.',
      
      // Search Form
      location: 'Location',
      pickupDate: 'Pickup Date',
      returnDate: 'Return Date',
      
      // Steps Section
      rentWithSteps: 'Rent with following 3 working steps',
      chooseLocation: 'Choose location',
      chooseLocationDesc: 'Choose your location and find your best car',
      pickupDateDesc: 'Select your pick up date and time to book your car',
      bookYourCar: 'Book your car',
      bookYourCarDesc: 'Book your car and we will deliver it directly to you',
      
      // Features
      whyChooseKiraCar: 'Why Choose KiraCar?',
      whyChooseSubtitle: 'Experience the best car rental service with our verified agencies and exceptional customer support.',
      verifiedAgencies: 'Verified Agencies',
      verifiedAgenciesDesc: 'All rental agencies are thoroughly verified for your safety and peace of mind.',
      support247: '24/7 Support',
      support247Desc: 'Round-the-clock customer support to assist you whenever you need help.',
      bestPrices: 'Best Prices',
      bestPricesDesc: 'Compare prices from multiple agencies to find the best deals on car rentals.',
      trustedReviews: 'Trusted Reviews',
      trustedReviewsDesc: 'Read genuine reviews from verified customers to make informed decisions.',
      
      // Sections
      featuredCars: 'Featured Cars',
      featuredCarsDesc: 'Discover our most popular rental cars from trusted agencies.',
      trustedAgencies: 'Trusted Agencies',
      trustedAgenciesDesc: 'Partner with the most reliable car rental agencies worldwide.',
      viewAllCars: 'View All Cars',
      
      // CTA
      readyToStart: 'Ready to Start Your Journey?',
      readyToStartDesc: 'Join thousands of satisfied customers and find your perfect rental car today.',
      browseCars: 'Browse Cars',
      joinAsAgency: 'Join as Agency',
      
      // Booking
      selectCar: 'Select Car',
      bookNow: 'Book Now',
      pricePerDay: 'per day',
      
      // Reviews
      reviews: 'Reviews',
      writeReview: 'Write a Review',
      rating: 'Rating',
      
      // Forms
      email: 'Email',
      password: 'Password',
      fullName: 'Full Name',
      phone: 'Phone',
      
      // Authentication
      signInToAccount: 'Sign in to your account to continue',
      welcomeBack: 'Welcome Back',
      enterCredentials: 'Enter your credentials to access your account',
      enterEmail: 'Enter your email',
      enterPassword: 'Enter your password',
      rememberMe: 'Remember me',
      forgotPassword: 'Forgot password?',
      signingIn: 'Signing in...',
      orContinueWith: 'Or continue with',
      continueWithGoogle: 'Continue with Google',
      dontHaveAccount: 'Don\'t have an account?',
      alreadyHaveAccount: 'Already have an account?',
      signInHere: 'Sign in here',
      
      // Signup
      joinKiraCar: 'Join KiraCar',
      createYourAccount: 'Create Your Account',
      chooseYourPath: 'Choose your path and start your journey with the best car rental platform',
      almostThere: 'Almost there! Let\'s set up your {{role}} account',
      customer: 'customer',
      customerRoleTitle: 'I want to rent cars',
      customerAccount: 'Customer Account',
      customerRoleDescription: 'Browse and book amazing cars from verified agencies',
      browseThousandsCars: 'Browse thousands of cars',
      instantBooking: 'Instant booking',
      securePayments: 'Secure payments',
      agencyRoleTitle: 'I want to rent out cars',
      agencyAccount: 'Agency Account',
      agencyRoleDescription: 'List your fleet and grow your rental business',
      listUnlimitedCars: 'List unlimited cars',
      manageBookings: 'Manage bookings',
      analyticsDashboard: 'Analytics dashboard',
      revenueTracking: 'Revenue tracking',
      startRentingToday: 'Start renting amazing cars today',
      growYourBusiness: 'Grow your rental business with us',
      change: 'Change',
      enterFullName: 'Enter your full name',
      enterPhone: 'Enter your phone number',
      createPassword: 'Create a strong password',
      confirmPassword: 'Confirm Password',
      confirmYourPassword: 'Confirm your password',
      creatingAccount: 'Creating Account...',
      createMyAccount: 'Create My Account',
      getStarted: 'Get Started',
      accountCreatedSuccess: 'Account created successfully! Please check your email to verify your account.',
      checkYourEmailForVerification: 'Please check your email to verify your account.',
      thankYouForJoining: 'Thank you for joining KiraCar! We\'ve sent a verification email to your inbox. Please click the link in the email to activate your account.',
      backToSignup: 'Back to Signup',
      
      // Cars page
      availableCars: 'Available Cars',
      findPerfectCar: 'Find the perfect car for your next journey',
      searchByMakeModel: 'Search by make or model',
      allCategories: 'All Categories',
      allTransmissions: 'All Transmissions',
      anySeats: 'Any Seats',
      clearFilters: 'Clear Filters',
      carsFound: 'cars found',
      noCarsFound: 'No cars found',
      adjustSearchCriteria: 'Try adjusting your search criteria or clear filters to see more results.',
      more: 'more',
      
      // Footer
      quickLinks: 'Quick Links',
      forAgencies: 'For Agencies',
      agencyHelp: 'Agency Help',
      termsOfService: 'Terms of Service',
      privacyPolicy: 'Privacy Policy',
      allRightsReserved: 'All rights reserved',
      
      // Customer Dashboard
      totalBookings: 'Total Bookings',
      noBookingsYet: 'No bookings yet',
      startExploring: 'Start exploring our cars to make your first booking!',
      recentBookings: 'Recent Bookings',
      viewDetails: 'View Details',
      bookNewCar: 'Book New Car'
    }
  },
  fr: {
    common: commonFr,
    dashboard: dashboardFr,
    vehicles: vehiclesFr,
    calendar: calendarFr,
    agency: agencyFr,
    auth: authFr,
    cars: carsFr,
    admin: adminFr,
    translation: {
      // Keep existing French translations
      ...commonFr,
      ...authFr,
      // Include the full French translation bundle with agenciesPage
      ...fr.translation,
      // Provide nested auth namespace for components using 'auth.xxx' keys
      auth: authFr,
      vehicles: vehiclesFr,
      agency: agencyFr,
      // Duplicate dashboard namespace under defaultNS for components using 'dashboard.xxx' keys
      dashboard: dashboardFr,
      // Duplicate common namespace under defaultNS for components using 'common.xxx' keys
      common: commonFr,
      // Duplicate cars namespace under defaultNS for components using 'cars.xxx' keys
      cars: carsFr,
      // Duplicate admin namespace under defaultNS for components using 'admin.xxx' keys
      admin: adminFr,
      // Add more French translations here if needed
    }
  },
  ar: {
    common: commonAr,
    dashboard: dashboardAr,
    vehicles: vehiclesAr,
    calendar: calendarAr,
    agency: agencyAr,
    auth: authAr,
    cars: carsAr,
    admin: adminAr,
    translation: {
      // Keep existing Arabic translations
      ...commonAr,
      ...authAr,
      // Include the full Arabic translation bundle with agenciesPage
      ...ar.translation,
      // Provide nested auth namespace for components using 'auth.xxx' keys
      auth: authAr,
      vehicles: vehiclesAr,
      agency: agencyAr,
      // Duplicate dashboard namespace under defaultNS for components using 'dashboard.xxx' keys
      dashboard: dashboardAr,
      // Duplicate common namespace under defaultNS for components using 'common.xxx' keys
      common: commonAr,
      // Duplicate cars namespace under defaultNS for components using 'cars.xxx' keys
      cars: carsAr,
      // Duplicate admin namespace under defaultNS for components using 'admin.xxx' keys
      admin: adminAr,
      // Add more Arabic translations here if needed
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'fr',
    debug: false,
    
    // Enable namespace support
    ns: ['translation', 'common', 'dashboard', 'vehicles', 'calendar', 'agency', 'auth', 'admin'],
    defaultNS: 'translation',
    
    interpolation: {
      escapeValue: false,
    },
    
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
    },
  })
  .then(() => {
    // Force add Arabic agency resource if it's missing
    if (!i18n.hasResourceBundle('ar', 'agency')) {
      i18n.addResourceBundle('ar', 'agency', agencyAr, true, true);
    }
    
    // Also ensure English and French resources are properly loaded
    if (!i18n.hasResourceBundle('en', 'agency')) {
      i18n.addResourceBundle('en', 'agency', agency, true, true);
    }
    
    if (!i18n.hasResourceBundle('fr', 'agency')) {
      i18n.addResourceBundle('fr', 'agency', agencyFr, true, true);
    }
    
    // Ensure admin resources are properly loaded
    if (!i18n.hasResourceBundle('en', 'admin')) {
      i18n.addResourceBundle('en', 'admin', admin, true, true);
    }
    
    if (!i18n.hasResourceBundle('fr', 'admin')) {
      i18n.addResourceBundle('fr', 'admin', adminFr, true, true);
    }
    
    if (!i18n.hasResourceBundle('ar', 'admin')) {
      i18n.addResourceBundle('ar', 'admin', adminAr, true, true);
    }
  });

export default i18n; 